import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useEffect } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getAddons,
  resetAddonFilters,
  setAddonFilter,
  updateAddonStatus,
} from "store/slices/membershipSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { CurrencySymbol, formatUTCDate, getMinMaxDate } from "utils/utils";

const Addons = () => {
  const { t } = useTranslation();
  const { trash, edit, noAddons, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("membership_addon");

  const {
    loading,
    isLoading,
    addons,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    addonFilters: { perPage, pageNo, status, orderBy, keyword, dates },
  } = useSelector((state) => state.Membership);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage,
      pageNo: page || pageNo,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getAddons(payload));
  };

  const handleReset = () => {
    dispatch(resetAddonFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
  ];

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <PageHeader title={t("Addons")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) =>
          dispatch(setAddonFilter({ key: "keyword", value }))
        }
        keywordPlaceholder={t("Search by Title")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy }}
        onFilterUpdate={(key, value) =>
          dispatch(setAddonFilter({ key, value }))
        }
        dates={dates}
        setDates={(value) => dispatch(setAddonFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Addon")}
        onBtnClick={() => navigate("/membership/addons/add-addon")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && addons?.length === 0 && (
        <NoDataFound title={t("No Addons Found")} icon={noAddons} />
      )}
      {!loading && addons?.length > 0 && (
        <div className="common_table_style">
          <Table hover responsive>
            <thead className="t_head">
              <tr>
                <th style={{ width: 70 }}>{t("S.No")}</th>
                <th>{t("Title")}</th>
                <th>{t("Price")}</th>
                <th
                  style={{
                    width: "30%",
                  }}
                >
                  {t("Description")}
                </th>
                <th>{t("Created Date")}</th>
                <th>{t("Status")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {addons.map((addon, index) => {
                const { _id, title, price, description, status, createdAt } =
                  addon || {};
                return (
                  <tr key={_id}>
                    <td>{index + 1}</td>

                    <td>
                      <div className="">
                        <Link to={`/membership/addons/addon-details?id=${_id}`}>
                          {title?.en}
                        </Link>
                      </div>

                      {addon.titleAr && (
                        <div className="text-muted small">{title?.ar}</div>
                      )}
                    </td>

                    <td>
                      <p className="price_symbol">
                        <CurrencySymbol width="12" height="14" />
                        {price ? `${price}` : "-"}
                      </p>
                    </td>

                    <td
                      style={{
                        width: "30%",
                      }}
                    >
                      <p className="text-ellipsis-3">
                        <small>{description.en}</small>
                      </p>
                    </td>

                    <td>
                      <div className="ltr_text">{formatUTCDate(createdAt)}</div>
                    </td>

                    <td>
                      <StatusBadge status={status} t={t} />
                    </td>
                    <td>
                      <div className="actions">
                        {canEdit && (
                          <>
                            <Switch
                              checked={status === 1}
                              handleOnChange={() => {
                                handleStatusUpdate({
                                  _id,
                                  status,
                                  dispatch,
                                  action: updateAddonStatus,
                                });
                              }}
                              disabled={isLoading && loadingItemId === _id}
                            />
                            <img
                              src={edit}
                              width={20}
                              height={20}
                              alt="edit"
                              onClick={() =>
                                navigate(
                                  `/membership/addons/edit-addon?id=${_id}`,
                                )
                              }
                            />
                          </>
                        )}
                        {canView && (
                          <Link to={`/membership/addons/addon-details?id=${_id}`}>
                            <img src={view} width={20} height={20} alt="view" />
                          </Link>
                        )}
                        {canDelete && (
                          <img
                            src={trash}
                            width={20}
                            height={20}
                            alt="delete"
                            onClick={() =>
                              dispatch(
                                showDeleteModal({
                                  title: t("Delete Addon"),
                                  message: t(
                                    "Are you sure you want to delete this Addon?",
                                  ),
                                  itemId: _id,
                                  actionType: "ADDON",
                                }),
                              )
                            }
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
      {!loading && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={getAllData}
        />
      )}
    </div>
  );
};

export default Addons;
